import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import "./DashboardCus.css";

const DashboardCus = () => {
  const [data, setData] = useState(null);
  const [customerSummary, setCustomerSummary] = useState([]);
  const [showModal] = useState(false);
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();
  const currentYear = now.getFullYear();

  const yearToUse =
    currentMonth === 11 && currentDay === 31 ? currentYear + 1 : currentYear;

  /* 🔒 [คงเดิม] ตัวเลือกวันที่รูปแบบเดิมของคุณ */
  const [startDate, setStartDate] = useState(new Date(yearToUse, 0, 1));
  const [endDate, setEndDate] = useState(new Date(yearToUse, 11, 31));
  const navigate = useNavigate();

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    const sorted = [...customerSummary].sort((a, b) => {
      const valA = a[key] ?? "";
      const valB = b[key] ?? "";

      if (typeof valA === "number" && typeof valB === "number") {
        return direction === "asc" ? valA - valB : valB - valA;
      }

      return direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });

    setCustomerSummary(sorted);
  };

  const loadData = useCallback(async () => {
    try {
      const startStr = `${startDate.getMonth() + 1}-${startDate.getFullYear()}`;
      const endStr = `${endDate.getMonth() + 1}-${endDate.getFullYear()}`;

      const res = await axios.get(
        `/dashboardcus?start=${startStr}&end=${endStr}`,
        { withCredentials: true },
      );

      setData(res.data);
    } catch (err) {
      console.error("Dashboard Error:", err);

      setData({
        summary: {
          total_tickets: 0,
          closed_within_sla: 0,
          violated_sla: 0,
          avg_star_year: 0,
        },
        tickets: [],
      });
    }
  }, [startDate, endDate]);

  const loadCustomerSummary = useCallback(async () => {
    try {
      const startStr = `${startDate.getMonth() + 1}-${startDate.getFullYear()}`;
      const endStr = `${endDate.getMonth() + 1}-${endDate.getFullYear()}`;

      const res = await axios.get(
        `/dashboardcus/all?start=${startStr}&end=${endStr}`,
        { withCredentials: true },
      );

      setCustomerSummary(res.data);
    } catch (err) {
      console.error("Dashboard List Error:", err);
      setCustomerSummary([]);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
    loadCustomerSummary();
  }, [loadData, loadCustomerSummary]);

  if (!data) return <div>Loading...</div>;

  const pieData = [
    { name: "Resolved on Time", value: data.summary.closed_within_sla },
    { name: "Resolved Late", value: data.summary.violated_sla },
  ];
  const COLORS = ["#11C26D", "#e71d36"];

  return (
    <>
      <div className="dash-container">
        <h2 className="cus-info-title">Ticket Incident Dashboard</h2>

        {/* Filters - [คงเดิม] ตัวเลือกวันที่แกนเดิมไม่ขยับ */}
        <div className="filters">
          <div className="filter-item">
            <label>Start Month</label>
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              dateFormat="MM/yyyy"
              showMonthYearPicker
            />
          </div>

          <div className="filter-item">
            <label>End Month</label>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              dateFormat="MM/yyyy"
              showMonthYearPicker
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-grid">
          <div className="summary-card">
            <h3>Total Tickets</h3>
            <p>{data.summary.total_tickets}</p>
          </div>

          <div className="summary-card green">
            <h3>Resolved on Time</h3>
            <p>{data.summary.closed_within_sla}</p>
          </div>

          <div className="summary-card red">
            <h3>Resolved Late</h3>
            <p>{data.summary.violated_sla}</p>
          </div>

          <div className="summary-card yellow">
            <h3>Avg Star</h3>
            <p>{Number(data.summary.avg_star_year).toFixed(2)}</p>
          </div>
        </div>

        <div className="chart-section-grid">
          {/* กราฟวงกลมภาพรวม SLA */}
          <div className="chart-card">
            <h3>SLA Status Overview</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 🛠️ กราฟแท่งเปรียบเทียบแต่ละบริษัท (อัปเกรดเพื่อรองรับ 20-30 บริษัทอย่างสมบูรณ์แบบ ไม่บีบอัด ไม่ซ้อน) */}
          <div className="chart-card large">
            <h3>Company Performance Comparison</h3>
            <div
              className="chart-container"
              style={{ maxHeight: "320px", overflowY: "auto", paddingRight: "5px" }}
            >
              <ResponsiveContainer
                width="100%"
                height={Math.max(250, customerSummary.length * 45)}
              >
                <BarChart
                  data={customerSummary}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />

                  <XAxis
                    type="number"
                    allowDecimals={false}
                    domain={[0, "auto"]}
                    tick={{ fontSize: 12 }}
                  />

                  <YAxis
                    type="category"
                    dataKey="company_name"
                    tick={{ fontSize: 11 }}
                    width={140} /* เพิ่มพื้นที่ด้านซ้ายให้อ่านชื่อยาวๆ อย่าง KIMPTON MAA-LAI BANGKOK ออกชัดเจน */
                  />

                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />

                  <Bar
                    dataKey="sla_ok"
                    name="On Time"
                    fill="#11C26D"
                    radius={[0, 4, 4, 0]}
                  />
                  <Bar
                    dataKey="sla_violated"
                    name="Late"
                    fill="#e71d36"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-section">
          <h3>Customer Incident Summary</h3>
          <table>
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("company_name")}
                  className="sortable"
                >
                  Company{" "}
                  {sortConfig.key === "company_name" &&
                    (sortConfig.direction === "asc" ? "▲" : "▼")}
                </th>

                <th
                  onClick={() => handleSort("total_tickets")}
                  className="sortable"
                >
                  Total Tickets{" "}
                  {sortConfig.key === "total_tickets" &&
                    (sortConfig.direction === "asc" ? "▲" : "▼")}
                </th>

                <th onClick={() => handleSort("sla_ok")} className="sortable">
                  Resolved on Time{" "}
                  {sortConfig.key === "sla_ok" &&
                    (sortConfig.direction === "asc" ? "▲" : "▼")}
                </th>

                <th
                  onClick={() => handleSort("sla_violated")}
                  className="sortable"
                >
                  Resolved Late{" "}
                  {sortConfig.key === "sla_violated" &&
                    (sortConfig.direction === "asc" ? "▲" : "▼")}
                </th>

                <th>More</th>
              </tr>
            </thead>

            <tbody>
              {customerSummary.map((c, i) => (
                <tr key={i}>
                  <td>{c.company_name}</td>
                  <td>{c.total_tickets}</td>
                  <td className="sla-ok">{c.sla_ok ?? "-"}</td>
                  <td className="sla-bad">{c.sla_violated ?? "-"}</td>

                  <td>
                    <button
                      className="detail-btn"
                      onClick={() =>
                        navigate(
                          `/admin/dashboard/customer-tickets/${c.company_id}`,
                          {
                            state: {
                              company_name: c.company_name,
                              start: startDate,
                              end: endDate,
                            },
                          },
                        )
                      }
                    >
                      View 
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showModal && <div className="modal-bg">Modal content</div>}
      </div>
    </>
  );
};

export default DashboardCus;